import {
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { FieldArray, FieldArrayRenderProps, useFormikContext } from 'formik'
import { PureAsyncSearchInput } from '../../../components/Formik/Forms/PureAsyncSearchInput'
import SelectInput from '../../../components/Formik/Forms/SelectInput'
import snackbarStore from '../../../stores/snackbar'
import { formatLabelPerson } from '../../pessoas/utils'
import {
  CenarioCondicao,
  CenarioMetadataResponse,
  TipoCondicaoCenario
} from '../types'

interface Props {
  metadata: CenarioMetadataResponse | null
}

const chaveCondicao = (condicao: CenarioCondicao) =>
  `${condicao.tipo}:${condicao.valorId ?? condicao.valorTexto}`

export function CondicoesInput({ metadata }: Props) {
  const { values, setFieldValue } = useFormikContext<{
    condicoes: CenarioCondicao[]
    _novaCondicaoTipo: string
    _novaCondicaoValor: any
  }>()

  const condicaoOptions = metadata
    ? Object.entries(metadata.condicoes).map(([tipo, m]) => ({
        value: tipo,
        label: m.label
      }))
    : []

  const tipoSelecionado = values._novaCondicaoTipo
  const metadataCondicao =
    metadata?.condicoes[tipoSelecionado as TipoCondicaoCenario]

  function handleChangeTipo(event: React.ChangeEvent<{ value: unknown }>) {
    setFieldValue('_novaCondicaoTipo', event.target.value)
    setFieldValue('_novaCondicaoValor', null)
  }

  function addCondicao(arrayHelpers: FieldArrayRenderProps) {
    return () => {
      if (!tipoSelecionado || !metadataCondicao) {
        snackbarStore.setMessage('Selecione o tipo de condição.')
        return
      }

      const valorBruto = values._novaCondicaoValor

      let novaCondicao: CenarioCondicao

      if (metadataCondicao.resourceUrl) {
        const selecionado = valorBruto as { name: string; value: string } | null

        if (!selecionado) {
          snackbarStore.setMessage('Selecione o valor da condição.')
          return
        }

        novaCondicao = metadataCondicao.isEntidade
          ? {
              tipo: tipoSelecionado as any,
              valorId: selecionado.value,
              rotulo: selecionado.name
            }
          : {
              tipo: tipoSelecionado as any,
              valorTexto: selecionado.name,
              rotulo: selecionado.name
            }
      } else {
        if (!valorBruto) {
          snackbarStore.setMessage('Selecione o valor da condição.')
          return
        }

        novaCondicao = {
          tipo: tipoSelecionado as any,
          valorTexto: valorBruto,
          rotulo: valorBruto
        }
      }

      const jaExiste = values.condicoes.some(
        condicao => chaveCondicao(condicao) === chaveCondicao(novaCondicao)
      )

      if (jaExiste) {
        snackbarStore.setMessage('Essa condição já foi adicionada.')
        return
      }

      arrayHelpers.push(novaCondicao)
      setFieldValue('_novaCondicaoTipo', '')
      setFieldValue('_novaCondicaoValor', null)
    }
  }

  const ehPessoa = metadataCondicao?.resourceUrl === '/pessoas'

  return (
    <FieldArray
      name="condicoes"
      render={arrayHelpers => (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5">Condições</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <SelectInput
              name="_novaCondicaoTipo"
              label="Tipo de Condição"
              options={condicaoOptions}
              onChange={handleChangeTipo}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            {metadataCondicao?.resourceUrl ? (
              ehPessoa ? (
                <PureAsyncSearchInput
                  name="_novaCondicaoValor"
                  label="Valor"
                  url={metadataCondicao.resourceUrl}
                  paramSearch="nomeDocumento"
                  labelFormat={formatLabelPerson}
                />
              ) : (
                <PureAsyncSearchInput
                  name="_novaCondicaoValor"
                  label="Valor"
                  url={metadataCondicao.resourceUrl}
                  paramSearch={metadataCondicao.paramSearch ?? 'nome'}
                  labelField={metadataCondicao.labelField ?? 'nome'}
                />
              )
            ) : metadataCondicao?.valoresFixos ? (
              <SelectInput
                name="_novaCondicaoValor"
                label="Valor"
                options={metadataCondicao.valoresFixos.map((valor: string) => ({
                  value: valor,
                  label: valor
                }))}
              />
            ) : null}
          </Grid>

          <Grid
            item
            xs={12}
            sm={2}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <Button
              variant="outlined"
              color="primary"
              type="button"
              fullWidth
              onClick={addCondicao(arrayHelpers)}
            >
              Adicionar
            </Button>
          </Grid>

          {!!values.condicoes.length && (
            <Grid item xs={12}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {values.condicoes.map((condicao, index) => (
                    <TableRow key={`${condicao.tipo}-${index}`}>
                      <TableCell>
                        {metadata?.condicoes[
                          condicao.tipo as TipoCondicaoCenario
                        ]?.label ?? condicao.tipo}
                      </TableCell>
                      <TableCell>{condicao.rotulo}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => arrayHelpers.remove(index)}>
                          <Tooltip title="Remover condição">
                            <DeleteIcon />
                          </Tooltip>
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          )}
        </Grid>
      )}
    />
  )
}
